import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { extractErrorMessage } from '../utils/errorUtils';
import {
  PageHeader,
  Button,
  Badge,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Pagination
} from '../components/common';

const BuildingUnits = () => {
  const { buildingId } = useParams();
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [building, setBuilding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    fetchBuilding();
    fetchUnits();
  }, [buildingId, currentPage]);

  const fetchBuilding = async () => {
    try {
      const response = await api.get(`/Building/${buildingId}`);
      setBuilding(response.data);
    } catch (err) {
      console.error('Error fetching building:', err);
    }
  };

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const params = {
        pageNumber: currentPage,
        pageSize: pageSize
      };

      const response = await api.get(`/Building/${buildingId}/units`, { params });
      console.log('Units API response:', response.data);
      setUnits(response.data.items || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      setError(extractErrorMessage(err));
      console.error('Error fetching units:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (unit) => {
    return unit.isActive
      ? { label: 'Active', variant: 'accent' }
      : { label: 'Inactive', variant: 'secondary' };
  };

  const getMembersBadge = (unit) => {
    if (unit.totalMembers === 0) {
      return { label: 'Vacant', variant: 'outline' };
    }
    return { label: `${unit.totalMembers} Member${unit.totalMembers > 1 ? 's' : ''}`, variant: 'info' };
  };

  if (loading && units.length === 0) {
    return <LoadingSpinner size="large" />;
  }

  console.log('Rendering BuildingUnits with units:', units, 'loading:', loading, 'error:', error);

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Building Units - ${building?.name || 'Loading...'}`}
        backLink={{ label: "Back to Building Details", href: `/buildings/${buildingId}` }}
        actions={[
          <Button
            key="members"
            onClick={() => navigate(`/buildings/${buildingId}/members`)}
            variant="accent"
          >
            View Members
          </Button>,
          <Button
            key="join-requests"
            onClick={() => navigate(`/buildings/${buildingId}/join-requests`)}
            variant="secondary"
          >
            Join Requests
          </Button>
        ]}
      />

      {error && (
        <ErrorMessage message={error} />
      )}

      <div>
        <ListContainer>
          {units.length === 0 ? (
            <EmptyState title="No units found" icon="🏠" description="This building currently has no units." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Unit</TableCell>
                  <TableCell header>Details</TableCell>
                  <TableCell header>Status</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {units.map((unit) => (
                  <TableRow key={unit.buildingUnitId || unit.number}>
                    <TableCell>
                      <div className="font-semibold text-slate-900">
                        Unit {unit.number}{unit.floor ? ` - Floor ${unit.floor}` : ''}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-700">
                        <div>{unit.description || 'No description'}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Created: {formatDate(unit.createdDate)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Badge
                          variant={getStatusBadge(unit).variant}
                          size="small"
                        >
                          {getStatusBadge(unit).label}
                        </Badge>
                        <Badge
                          variant={getMembersBadge(unit).variant}
                          size="small"
                        >
                          {getMembersBadge(unit).label}
                        </Badge>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ListContainer>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default BuildingUnits;